<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';
import IspChartjsLine from './components/IspChartjsLine.vue';
// GK-HUB: Central conversation modal used by the CSAT drill-down
import IspConversationDetailModal from './components/IspConversationDetailModal.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
import { formatTrend, getDateLocale } from './composables/ispFormatters';
import { getUserRole } from 'dashboard/helper/permissionsHelper';

const store = useStore();
const { t, locale } = useI18n();

const {
  data: csat,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filteredAgents,
} = useIspReport({
  getter: 'ispReports/getCsat',
  action: 'ispReports/fetchCsat',
  filters: { agentId: '', teamId: '', rating: '', page: '1' },
  initActions: ['agents/get'],
});

// Team filter (same pattern as IspFcr)
const teams = computed(() => store.getters['teams/getTeams']);
const selectedTeamId = ref('');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const userRole = computed(() =>
  getUserRole(currentUser.value, currentAccountId.value)
);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(userRole.value)
);

onMounted(() => {
  store.dispatch('teams/get');
});

const onTeamChange = e => {
  selectedTeamId.value = e.target.value;
  onFilterChange('teamId', e);
};

// Rating filter
const selectedRating = ref('');
const onRatingChange = e => {
  selectedRating.value = e.target.value;
  onFilterChange('rating', e);
};

const summary = computed(() => csat.value.data?.summary || {});

// Rating color helper
const getRatingColor = rating => {
  if (rating >= 5) return 'teal';
  if (rating >= 4) return 'blue';
  if (rating >= 3) return 'warning';
  return 'error';
};

// Distribution bar items
const barItems = computed(() =>
  (csat.value.data?.distribuicao || []).map(d => ({
    label: `${d.rating}★ ${t(`ISP_REPORTS.CSAT.RATING_${d.rating}`)}`,
    value: String(d.count),
    displayValue: String(d.count),
    percentage: d.percentage,
    color: getRatingColor(d.rating),
  }))
);

// Evolution chart series
const chartSeries = computed(() => {
  const evolucao = csat.value.data?.evolucao || [];
  if (!evolucao.length) return [];
  return [
    {
      name: 'CSAT',
      color: '#5DADE2',
      data: evolucao.map(e => ({
        value: e.nota_media,
        label: new Date(e.timestamp * 1000).toLocaleDateString(
          getDateLocale(locale.value),
          {
            month: 'short',
          }
        ),
      })),
    },
  ];
});

// Agent table columns
const agentColumns = [
  // GK-HUB: type 'agent' renders the IA badge inline when row.is_ia_agent is true
  { key: 'agent_name', label: t('ISP_REPORTS.CSAT.COL_AGENT'), type: 'agent' },
  {
    key: 'avaliacoes',
    label: t('ISP_REPORTS.CSAT.COL_AVALIACOES'),
    align: 'right',
    type: 'mono',
  },
  {
    key: 'nota_media',
    label: t('ISP_REPORTS.CSAT.COL_NOTA_MEDIA'),
    align: 'right',
    type: 'badge',
  },
  {
    key: 'satisfacao',
    label: t('ISP_REPORTS.CSAT.COL_SATISFACAO'),
    align: 'right',
    type: 'mono',
  },
  {
    key: 'trend',
    label: t('ISP_REPORTS.CSAT.COL_TREND'),
    align: 'right',
    type: 'trend',
  },
];

const agentRows = computed(() =>
  (csat.value.data?.por_agente || []).map(a => ({
    // GK-HUB: keep agent_id for drill-down click handler
    __agent_id: a.agent_id,
    // GK-HUB: IA badge rendered by IspDataTable 'agent' column type
    is_ia_agent: a.is_ia_agent,
    agent_name: a.agent_name,
    avaliacoes: a.avaliacoes,
    nota_media: Number(a.nota_media || 0).toFixed(1),
    satisfacao: `${a.satisfacao}%`,
    trend: formatTrend(a.trend),
  }))
);

// Inbox table columns
const inboxColumns = [
  { key: 'inbox_name', label: t('ISP_REPORTS.CSAT.COL_INBOX') },
  {
    key: 'avaliacoes',
    label: t('ISP_REPORTS.CSAT.COL_AVALIACOES'),
    align: 'right',
    type: 'mono',
  },
  {
    key: 'nota_media',
    label: t('ISP_REPORTS.CSAT.COL_NOTA_MEDIA'),
    align: 'right',
    type: 'badge',
  },
  {
    key: 'satisfacao',
    label: t('ISP_REPORTS.CSAT.COL_SATISFACAO'),
    align: 'right',
    type: 'mono',
  },
];

const inboxRows = computed(() =>
  (csat.value.data?.por_inbox || []).map(i => ({
    // GK-HUB: keep inbox_id for drill-down click handler
    __inbox_id: i.inbox_id,
    inbox_name: i.inbox_name,
    avaliacoes: i.avaliacoes,
    nota_media: Number(i.nota_media || 0).toFixed(1),
    satisfacao: `${i.satisfacao}%`,
  }))
);

// Feedbacks table
const feedbacks = computed(() => csat.value.data?.feedbacks || {});
const feedbackRows = computed(() => feedbacks.value.records || []);
const feedbackPagination = computed(() => feedbacks.value.pagination || {});

const goToPage = page => {
  onFilterChange('page', String(page));
};

// Stars helper
const renderStars = rating => {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
};

// Date formatter
const formatDate = iso => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(getDateLocale(locale.value), {
    day: '2-digit',
    month: '2-digit',
  });
};

// ── Drill-down: drawer + central modal ──
// GK-HUB: Click in "Por Agente" or "Por Inbox" tables opens a right drawer
// listing all CSAT responses for that agent/inbox. Clicking a response opens
// the central conversation modal (read-only).
const drawerOpen = ref(false);
const drawerType = ref(''); // 'agent' | 'inbox'
const drawerTargetId = ref(null);
const drawerTargetName = ref('');

const drillDownState = computed(
  () => store.getters['ispReports/getCsatResponses'] || {}
);
const drillDownData = computed(() => drillDownState.value.data || null);
const drillDownFetching = computed(
  () => drillDownState.value.isFetching || false
);
const drillDownRecords = computed(() => drillDownData.value?.records || []);
const drillDownPagination = computed(
  () => drillDownData.value?.pagination || {}
);

const fromTs = computed(() =>
  customDateRange.value?.[0]
    ? Math.floor(customDateRange.value[0].getTime() / 1000)
    : undefined
);
const toTs = computed(() =>
  customDateRange.value?.[1]
    ? Math.floor(customDateRange.value[1].getTime() / 1000)
    : undefined
);

const fetchDrillDownPage = (page = 1) => {
  const params = {
    from: fromTs.value,
    to: toTs.value,
    teamId: selectedTeamId.value || undefined,
    rating: selectedRating.value || undefined,
    page,
  };
  if (drawerType.value === 'agent') params.agentId = drawerTargetId.value;
  if (drawerType.value === 'inbox') params.inboxId = drawerTargetId.value;
  store.dispatch('ispReports/fetchCsatResponses', params);
};

const openAgentDrawer = row => {
  drawerType.value = 'agent';
  // eslint-disable-next-line no-underscore-dangle
  drawerTargetId.value = row.__agent_id;
  drawerTargetName.value = row.agent_name;
  drawerOpen.value = true;
  fetchDrillDownPage(1);
};

const openInboxDrawer = row => {
  drawerType.value = 'inbox';
  // eslint-disable-next-line no-underscore-dangle
  drawerTargetId.value = row.__inbox_id;
  drawerTargetName.value = row.inbox_name;
  drawerOpen.value = true;
  fetchDrillDownPage(1);
};

const closeDrawer = () => {
  drawerOpen.value = false;
  drawerType.value = '';
  drawerTargetId.value = null;
  drawerTargetName.value = '';
  store.dispatch('ispReports/clearCsatResponses');
};

const goToDrillPage = page => fetchDrillDownPage(page);

// Central modal state (shared, opens on top of drawer)
const selectedConversationId = ref(null);
const openConversationModal = response => {
  selectedConversationId.value = response.conversation_id;
};
const closeConversationModal = () => {
  selectedConversationId.value = null;
};

// ESC closes modal first, then drawer
const onEscape = e => {
  if (e.key !== 'Escape') return;
  if (selectedConversationId.value) {
    closeConversationModal();
  } else if (drawerOpen.value) {
    closeDrawer();
  }
};
onMounted(() => {
  document.addEventListener('keydown', onEscape);
});

// Star rendering helper
const renderStarsFull = n => '★'.repeat(n) + '☆'.repeat(5 - n);

// Export CSV
const onExportCsv = () => {
  const rows = csat.value.data?.por_agente || [];
  exportCsv(
    rows,
    'Agente,Avaliações,Nota Média,Satisfação (%),Tendência\n',
    r =>
      [r.agent_name, r.avaliacoes, r.nota_media, r.satisfacao, r.trend || 0]
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(','),
    'csat-report.csv'
  );
};
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('ISP_REPORTS.CSAT.HEADER') }}
        </h1>
        <p class="text-[13px] text-n-slate-10 mt-0.5">
          {{ t('ISP_REPORTS.CSAT.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('agentId', e)">
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_AGENTS') }}
          </option>
          <option
            v-for="agent in filteredAgents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- Team filter (admin/manager only) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          v-model="selectedTeamId"
          class="isp-select"
          @change="onTeamChange"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- Rating filter -->
      <div class="isp-select-wrap">
        <select
          v-model="selectedRating"
          class="isp-select"
          @change="onRatingChange"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_RATINGS') }}
          </option>
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <option v-for="r in [5, 4, 3, 2, 1]" :key="r" :value="r">
            {{ r }}★ {{ t(`ISP_REPORTS.CSAT.RATING_${r}`) }}
          </option>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <button
        class="btn-export border-n-slate-7 text-n-slate-10 hover:border-n-slate-9 hover:text-n-slate-12"
        @click="onExportCsv"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading (initial only) -->
    <woot-loading-state
      v-if="csat.isFetching && !csat.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (initial only) -->
    <div
      v-else-if="csat.error && !csat.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="csat.data">
      <!-- 5 KPI Cards -->
      <div
        class="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 mb-6"
      >
        <IspMetricCard
          :label="t('ISP_REPORTS.CSAT.NOTA_MEDIA')"
          :value="String(summary.nota_media || 0)"
          :trend="`${t('ISP_REPORTS.CSAT.VS_PREVIOUS')}`"
          :trend-direction="
            (summary.nota_media_trend || 0) >= 0 ? 'up' : 'down'
          "
          value-color="text-n-teal-11"
          :loading="csat.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CSAT.SATISFACAO')"
          :value="`${summary.satisfacao || 0}%`"
          value-color="text-n-blue-11"
          :loading="csat.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CSAT.TAXA_RESPOSTA')"
          :value="`${summary.taxa_resposta || 0}%`"
          value-color="text-n-slate-12"
          :loading="csat.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CSAT.TOTAL_RESPOSTAS')"
          :value="String(summary.total_respostas || 0)"
          :trend="`${t('ISP_REPORTS.CSAT.VS_PREVIOUS')}`"
          :trend-direction="
            (summary.total_respostas_trend || 0) >= 0 ? 'up' : 'down'
          "
          value-color="text-n-slate-12"
          :loading="csat.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CSAT.DETRATORES')"
          :value="`${summary.detratores || 0}%`"
          value-color="text-red-500"
          :loading="csat.isFetching"
        />
      </div>

      <!-- 60/40 Charts Grid -->
      <div class="csat-charts-grid">
        <!-- Distribution (60%) -->
        <IspBarChart
          :title="t('ISP_REPORTS.CSAT.DISTRIBUICAO')"
          :subtitle="t('ISP_REPORTS.CSAT.DISTRIBUICAO_SUBTITLE')"
          :items="barItems"
        />

        <!-- Evolution (40%) — no goal line per user request -->
        <IspChartjsLine
          v-if="chartSeries.length"
          :title="t('ISP_REPORTS.CSAT.EVOLUCAO')"
          :series="chartSeries"
          :show-legend="false"
          :height="260"
        />
      </div>

      <!-- CSAT by Agent + CSAT by Inbox (50/50 side by side) -->
      <!-- GK-HUB: rows are clickable to open drill-down drawer -->
      <div class="csat-tables-grid">
        <div class="csat-table-cell">
          <IspDataTable
            :title="t('ISP_REPORTS.CSAT.POR_AGENTE')"
            :columns="agentColumns"
            :rows="agentRows"
            clickable-rows
            @row-click="openAgentDrawer"
          />
        </div>
        <div class="csat-table-cell">
          <IspDataTable
            :title="t('ISP_REPORTS.CSAT.POR_INBOX')"
            :columns="inboxColumns"
            :rows="inboxRows"
            clickable-rows
            @row-click="openInboxDrawer"
          />
        </div>
      </div>

      <!-- Feedbacks Table -->
      <div v-if="feedbackRows.length" class="csat-feedbacks-section">
        <div class="text-[13px] font-semibold text-n-slate-12 mb-3">
          {{ t('ISP_REPORTS.CSAT.FEEDBACKS') }}
        </div>
        <div
          class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
        >
          <div class="csat-feedbacks-scroll">
            <table class="w-full text-sm">
              <thead>
                <tr class="csat-feedback-thead">
                  <th
                    class="text-left px-4 py-2.5 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('ISP_REPORTS.CSAT.COL_CONTATO') }}
                  </th>
                  <th
                    class="text-center px-4 py-2.5 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('ISP_REPORTS.CSAT.COL_NOTA') }}
                  </th>
                  <th
                    class="text-left px-4 py-2.5 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('ISP_REPORTS.CSAT.COL_COMENTARIO') }}
                  </th>
                  <th
                    class="text-left px-4 py-2.5 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('ISP_REPORTS.CSAT.COL_AGENT') }}
                  </th>
                  <th
                    class="text-right px-4 py-2.5 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('ISP_REPORTS.CSAT.COL_DATA') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="fb in feedbackRows"
                  :key="fb.id"
                  class="border-t border-n-weak/50 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                  @click="openConversationModal(fb)"
                >
                  <td
                    class="px-4 py-2.5 text-n-slate-12 font-medium whitespace-nowrap"
                  >
                    {{ fb.contact_name }}
                  </td>
                  <td class="px-4 py-2.5 text-center whitespace-nowrap">
                    <span
                      class="csat-stars"
                      :class="`csat-stars--${fb.rating}`"
                    >
                      {{ renderStars(fb.rating) }}
                    </span>
                  </td>
                  <td
                    class="px-4 py-2.5 text-n-slate-11 max-w-[300px] truncate"
                  >
                    <span v-if="fb.feedback_message">
                      {{ fb.feedback_message }}
                    </span>
                    <span v-else class="italic text-n-slate-9">
                      {{ t('ISP_REPORTS.CSAT.DRILL_NO_COMMENT') }}
                    </span>
                  </td>
                  <td class="px-4 py-2.5 text-n-slate-10 whitespace-nowrap">
                    <span class="inline-flex items-center gap-1">
                      {{ fb.agent_name }}
                      <!-- GK-HUB: IA badge in feedbacks row -->
                      <span v-if="fb.is_ia_agent" class="gk-ia-badge">
                        {{ $t('CONVERSATION.IA_BADGE') }}
                      </span>
                    </span>
                  </td>
                  <td
                    class="px-4 py-2.5 text-right text-n-slate-10 font-mono text-xs whitespace-nowrap"
                  >
                    {{ formatDate(fb.created_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            v-if="feedbackPagination.total_pages > 1"
            class="flex items-center justify-between px-4 py-3 border-t border-n-weak/50"
          >
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <span class="text-xs text-n-slate-10">
              {{ feedbackPagination.total_count }} feedbacks
            </span>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
            <div class="flex items-center gap-1">
              <button
                v-for="p in feedbackPagination.total_pages"
                :key="p"
                class="csat-page-btn"
                :class="{
                  'csat-page-btn--active':
                    p === feedbackPagination.current_page,
                }"
                @click="goToPage(p)"
              >
                {{ p }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <div
      v-else-if="!csat.isFetching && !csat.error"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_MESSAGE') }}
      </p>
    </div>

    <!-- GK-HUB: Drill-down drawer (right) — list of CSAT responses for agent/inbox -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="drawerOpen"
          class="fixed inset-0 bg-black/30 z-40"
          @click="closeDrawer"
        />
      </Transition>
      <Transition name="slide">
        <div
          v-if="drawerOpen"
          class="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-n-solid-2 border-l border-n-weak shadow-xl flex flex-col"
        >
          <!-- Header -->
          <div
            class="sticky top-0 bg-n-solid-2 z-10 border-b border-n-weak px-5 py-4 flex-shrink-0"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div
                  class="text-[10px] uppercase tracking-wider text-n-slate-9 mb-1"
                >
                  {{
                    drawerType === 'agent'
                      ? t('ISP_REPORTS.CSAT.DRILL_AGENT_LABEL')
                      : t('ISP_REPORTS.CSAT.DRILL_INBOX_LABEL')
                  }}
                </div>
                <h2 class="text-base font-semibold text-n-slate-12 truncate">
                  {{ drawerTargetName }}
                </h2>
                <div class="text-[11px] text-n-slate-9 mt-1">
                  {{
                    t('ISP_REPORTS.CSAT.DRILL_TOTAL_COUNT', {
                      count: drillDownPagination.total_count || 0,
                    })
                  }}
                </div>
              </div>
              <button
                class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-solid-3 transition-colors cursor-pointer flex-shrink-0"
                :aria-label="t('ISP_REPORTS.CLOSE')"
                @click="closeDrawer"
              >
                <span class="i-lucide-x w-4 h-4 text-n-slate-10" />
              </button>
            </div>
          </div>

          <!-- Loading -->
          <div
            v-if="drillDownFetching && !drillDownData"
            class="flex items-center justify-center h-64"
          >
            <woot-loading-state :message="t('ISP_REPORTS.LOADING')" />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!drillDownRecords.length"
            class="flex flex-col items-center justify-center py-12 gap-2 text-center"
          >
            <div class="text-sm text-n-slate-9">
              {{ t('ISP_REPORTS.CSAT.DRILL_EMPTY') }}
            </div>
          </div>

          <!-- Records list -->
          <!-- GK-HUB: cards com bg n-solid-3 (contraste com drawer n-solid-2)
               + estrelas inline ao lado do nome para layout mais denso -->
          <div v-else class="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
            <button
              v-for="r in drillDownRecords"
              :key="r.id"
              class="w-full text-left bg-n-solid-3 border border-n-weak rounded-lg px-4 py-3 hover:bg-n-alpha-2 hover:border-n-slate-7 transition-colors flex items-start gap-3 cursor-pointer"
              @click="openConversationModal(r)"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span
                    class="text-[13px] font-semibold text-n-slate-12 truncate"
                  >
                    {{ r.contact_name }}
                  </span>
                  <!-- GK-HUB: estrelas inline (baseline alinhado com o nome) -->
                  <span
                    class="csat-stars text-[13px] leading-none flex-shrink-0"
                    :class="`csat-stars--${r.rating}`"
                    :title="`${r.rating}/5`"
                  >
                    {{ renderStarsFull(r.rating) }}
                  </span>
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <span
                    v-if="r.conversation_display_id"
                    class="text-[10px] font-mono text-n-blue-11 tabular-nums flex-shrink-0 ml-auto"
                  >
                    #GKC-{{ r.conversation_display_id }}
                  </span>
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                </div>
                <div
                  v-if="r.feedback_message"
                  class="text-[12px] text-n-slate-11 line-clamp-2 mb-1.5"
                >
                  {{ r.feedback_message }}
                </div>
                <div v-else class="text-[11px] text-n-slate-9 italic mb-1.5">
                  {{ t('ISP_REPORTS.CSAT.DRILL_NO_COMMENT') }}
                </div>
                <div class="flex items-center gap-2 text-[10px] text-n-slate-9">
                  <span
                    v-if="drawerType !== 'agent' && r.agent_name"
                    class="inline-flex items-center gap-1 truncate"
                  >
                    {{ r.agent_name }}
                    <!-- GK-HUB: IA badge in drill-down row -->
                    <span v-if="r.is_ia_agent" class="gk-ia-badge">
                      {{ $t('CONVERSATION.IA_BADGE') }}
                    </span>
                    <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                    <span>·</span>
                  </span>
                  <span
                    v-if="drawerType !== 'inbox' && r.inbox_name"
                    class="truncate"
                  >
                    <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                    {{ r.inbox_name }} ·
                  </span>
                  <span>{{ formatDate(r.created_at) }}</span>
                </div>
              </div>
              <span
                class="i-lucide-chevron-right w-4 h-4 text-n-slate-9 flex-shrink-0 mt-0.5"
              />
            </button>
          </div>

          <!-- Pagination -->
          <div
            v-if="drillDownPagination.total_pages > 1"
            class="flex items-center justify-between px-4 py-3 border-t border-n-weak bg-n-solid-3 text-[12px] text-n-slate-10 flex-shrink-0"
          >
            <span>
              {{
                t('ISP_REPORTS.PAGINATION.SHOWING', {
                  from:
                    (drillDownPagination.current_page - 1) *
                      drillDownPagination.per_page +
                    1,
                  to: Math.min(
                    drillDownPagination.current_page *
                      drillDownPagination.per_page,
                    drillDownPagination.total_count
                  ),
                  total: drillDownPagination.total_count,
                })
              }}
            </span>
            <div class="flex items-center gap-1">
              <button
                v-for="p in drillDownPagination.total_pages"
                :key="p"
                class="csat-page-btn"
                :class="{
                  'csat-page-btn--active':
                    p === drillDownPagination.current_page,
                }"
                @click="goToDrillPage(p)"
              >
                {{ p }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- GK-HUB: Central conversation modal (opens on top of drawer) -->
    <IspConversationDetailModal
      :conversation-id="selectedConversationId"
      @close="closeConversationModal"
    />
  </div>
</template>

<style scoped>
/* GK-HUB: CSS-11 .isp-select styles moved to _isp-shared.scss */

/* ── Export button ──────────────────────────────────────────────────── */
.btn-export {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border-width: 1.5px;
  border-style: solid;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

/* ── 60/40 Charts grid ─────────────────────────────────────────────── */
.csat-charts-grid {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 20px;
  margin-bottom: 24px;
  align-items: stretch;
}
.csat-charts-grid > :deep(*) {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
.csat-charts-grid > :deep(*) > div:last-child {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.csat-charts-grid > :deep(*) > div:last-child > div {
  flex: 1;
}

/* ── Tables grid: Agent + Inbox side by side (50/50) ───────────────── */
.csat-tables-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
  align-items: stretch;
}
.csat-table-cell {
  display: flex;
  flex-direction: column;
}
.csat-table-cell > :deep(*) {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: 0;
}
.csat-table-cell :deep(.overflow-x-auto) {
  max-height: 400px;
  overflow-y: auto;
}
.csat-table-cell :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* ── Feedbacks table ───────────────────────────────────────────────── */
.csat-feedbacks-section {
  margin-bottom: 24px;
}
.csat-feedbacks-scroll {
  max-height: 480px;
  overflow-y: auto;
}
.csat-feedback-thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--n-solid-2, #fafbfc);
}

/* ── Stars colors ──────────────────────────────────────────────────── */
.csat-stars {
  font-size: 13px;
  letter-spacing: 1px;
}
.csat-stars--5 {
  color: #10b981;
}
.csat-stars--4 {
  color: #5dade2;
}
.csat-stars--3 {
  color: #f59e0b;
}
.csat-stars--2 {
  color: #ef4444;
}
.csat-stars--1 {
  color: #ef4444;
}

/* ── Pagination ────────────────────────────────────────────────────── */
.csat-page-btn {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 150ms ease;
}
.csat-page-btn:hover {
  background: rgba(148, 163, 184, 0.08);
}
.csat-page-btn--active {
  background: rgba(93, 173, 226, 0.12);
  color: #2e86c1;
  border-color: rgba(93, 173, 226, 0.2);
  font-weight: 600;
}

/* ── DatePicker calendar fix ───────────────────────────────────────── */
:deep(.date-picker) {
  position: relative;
  z-index: 20;
}
:deep(.date-picker .datepicker--container),
:deep(.date-picker [class*='absolute']) {
  z-index: 50 !important;
}
:deep(.daterange-picker--container),
:deep(.datepicker--container) {
  z-index: 50 !important;
}

@media (max-width: 768px) {
  .csat-charts-grid {
    grid-template-columns: 1fr;
  }
  .csat-tables-grid {
    grid-template-columns: 1fr;
  }
}

/* GK-HUB: Drill-down drawer transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* GK-HUB: 2-line clamp for feedback preview in drawer */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<!-- GK-HUB: CSS-11 dark mode .isp-select styles moved to _isp-shared.scss -->
<style>
/* ── Feedbacks thead dark ────────────────────────────────────────── */
body.dark .csat-feedback-thead {
  background: var(--n-solid-2, #1e1e2e);
}

/* ── Pagination dark ─────────────────────────────────────────────── */
body.dark .csat-page-btn {
  color: #94a3b8;
}
body.dark .csat-page-btn:hover {
  background: rgba(255, 255, 255, 0.05);
}
body.dark .csat-page-btn--active {
  background: rgba(93, 173, 226, 0.15);
  color: #5dade2;
  border-color: rgba(93, 173, 226, 0.25);
}
</style>
